// Tracking line graph (ECharts).
//
// Renders the line chart used across ~14 tracking pages (weight, blood
// glucose, ketones, sleep, mood, insulin, blood pressure, etc.). It keeps the
// exact same public interface as the previous chart.js implementation — same
// props, emitted events, EventBus hooks and `loadData` / `renderChartFromData`
// methods — so GraphManager and every wrapper component are untouched.

import * as echarts from 'echarts'
import { TweenLite } from 'gsap/all'
import 'gsap/ScrollToPlugin'

import GraphManager from '../../../../helpers/GraphManager'
import clinicalRanges from '../../../../helpers/GraphManager/clinicalRanges'
import config from '../../../../config'
import MedicationRegimeLocalizerMixin from '../../../../mixins/Localization/MedicationRegimes'
import store from '../../../../store'

let fromDate = moment().locale(config.language).startOf('month');
let toDate = moment().locale(config.language).endOf('month');

const AXIS_COLOR = '#767576';
const GRID_COLOR = '#E3E9EC';
const FONT = 'Poppins';

// Tracking types where only one reading per day is clinically meaningful, so
// same-day entries are collapsed to the latest. (Intentionally excludes blood
// glucose, ketones, insulin, blood pressure, mood, sleep — multiple readings
// across a day matter there.)
const ONE_PER_DAY_TYPES = ['weight', 'waist-hip'];

// Distinct, readable line colours used when a series has no usable colour (or a
// white/near-white one) — guarantees nothing renders invisibly on white.
const FALLBACK_PALETTE = ['#3D96DE', '#FEA228', '#0F3C43', '#AE3463', '#34CAB9', '#9534CA'];

// Themed colour per tracking type, used as the first-choice fallback so a
// white/missing series colour still matches the chart's branding (e.g. mood
// should be purple, not generic blue).
const TYPE_BRAND_COLORS = {
  'weight': '#3D96DE',
  'waist-hip': '#3D96DE',
  'blood-glucose': '#E43229',
  'hba1c': '#E43229',
  'cholesterol': '#697DCE',
  'ketones': '#F5950C',
  'medication': '#AE3463',
  'sleep': '#FEA228',
  'sleep-wearable': '#FEA228',
  'blood-pressure': '#3693DE',
  'insulin': '#34CAB9',
  'mood': '#8150E4',
  'activity': '#4E2288',
  'activity-steps': '#4E2288'
};

export default {
  name: 'line-graph',
  mixins: [MedicationRegimeLocalizerMixin],
  props: {
    showPoints: false,
    type: {
      type: String,
    },
    dateStep: 0,
    userId: 0,
    medTrackingId: 0,
    activeStepsType: '',
    activeSleepType: '',
    fromDate: {
      type: String,
      default: fromDate.format('YYYY-MM-DD')
    },
    toDate: {
      type: String,
      default: toDate.format('YYYY-MM-DD')
    }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null,
      pointsVisible: false,
      // raw payload kept around for tooltip lookups (e.g. waist-hip ratio)
      rawResponse: null,
      data: [],
      labels: [],
      series: [],
      override: [],
      labelsAreDates: false,
      axisSpanMs: 0
    }
  },
  watch: {
    userId() { this.loadData(); },
    fromDate() { this.loadData(); },
    toDate() { this.loadData(); },
    medTrackingId() { this.loadData(); },
    activeStepsType() { this.loadData(); },
    activeSleepType() { this.loadData(); },
    showPoints(val) { this.setPointsVisible(val === true); },
    // Live-apply the display options from the Options menu without refetching.
    '$store.state.graphOptions.smoothLine'() { this.refreshDisplay(); },
    '$store.state.graphOptions.showArea'() { this.refreshDisplay(); }
  },
  render(h) {
    return h('div', {
      ref: 'chartEl',
      staticStyle: { width: '100%', minHeight: '200px' }
    });
  },
  mounted() {
    let self = this;
    const mainContentElement = document.getElementsByClassName('main-content')[0];

    self.pointsVisible = self.showPoints === true;
    self.loadData();

    self.$EventBus.$on('showGraphPoints', function () { self.setPointsVisible(true); });
    self.$EventBus.$on('hideGraphPoints', function () { self.setPointsVisible(false); });
    self.$EventBus.$on('updateGraphData', function () { self.loadData(); });
    self.$EventBus.$on('updateGraph', function () {
      if (mainContentElement) TweenLite.to(mainContentElement, 0.2, { scrollTo: 0 });
      self.loadData();
    });

    window.addEventListener('resize', self.handleResize);
  },
  beforeDestroy() {
    this.$EventBus.$off('updateGraph');
    this.$EventBus.$off('updateGraphData');
    this.$EventBus.$off('showGraphPoints');
    this.$EventBus.$off('hideGraphPoints');
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    loadData() {
      this.$emit('graphLoading', true);
      GraphManager.handleGraphType(this);
    },

    ensureChart() {
      let self = this;
      if (self.chart || !self.$refs.chartEl) return;
      self.chart = echarts.init(self.$refs.chartEl, null, { renderer: 'canvas' });

      // The graph mounts inside a `v-show`-hidden container, so its size can be
      // 0 at init time. A ResizeObserver lets ECharts catch up once it becomes
      // visible (or the window changes).
      if (typeof ResizeObserver !== 'undefined') {
        self.resizeObserver = new ResizeObserver(function () { self.handleResize(); });
        self.resizeObserver.observe(self.$refs.chartEl);
      }
    },

    handleResize() {
      if (this.chart) this.chart.resize();
    },

    setPointsVisible(visible) {
      this.pointsVisible = visible;
      if (!this.chart) return;
      const option = this.chart.getOption();
      if (!option || !option.series) return;
      option.series.forEach(function (s) { s.showSymbol = visible; });
      this.chart.setOption({ series: option.series });
    },

    // Re-apply the curved-line / area-fill options to the live chart without a
    // refetch. Mutates the existing series in place so clinical markArea /
    // markLine decorations are preserved.
    refreshDisplay() {
      const self = this;
      if (!self.chart) return;
      const option = self.chart.getOption();
      if (!option || !option.series) return;
      const smooth = store.state.graphOptions.smoothLine ? 0.4 : false;
      const showArea = store.state.graphOptions.showArea && self.series.length === 1;
      option.series.forEach(function (s, i) {
        const dashed = self.isDashed(i);
        s.smooth = smooth;
        s.areaStyle = (showArea && !dashed)
          ? { color: self.areaGradient(self.seriesColor(i)) }
          : { opacity: 0 };
      });
      self.chart.setOption({ series: option.series });
    },

    // --- x axis helpers -----------------------------------------------------

    // Axis label / tooltip granularity. For the fixed range pills we key off the
    // selected step directly so each reads exactly as the pill promises:
    //   Day   -> HH:mm   (time of day)
    //   Week  -> ddd     (Mon … Sun)
    //   Month -> D MMM   (each day)
    //   Year  -> MMM     (each month)
    // Custom ranges have an arbitrary step, so they fall back to picking a
    // format from the actual span shown — that way a wide range (e.g. 10 years)
    // doesn't collapse every year-boundary tick to an identical "01 Jan".
    timeFormatPattern() {
      switch (this.dateStep) {
        case 0:
        case 1:
          return 'HH:mm';
        case 7:
          return 'ddd';
        case 30:
          return 'D MMM';
        case 360:
          return 'MMM';
      }
      const DAY = 24 * 3600 * 1000;
      const span = this.axisSpanMs || 0;
      if (span > 0 && span <= 2 * DAY) return 'HH:mm';     // within a day or two
      if (span <= 95 * DAY) return 'DD MMM';               // up to ~3 months
      if (span <= 3 * 365 * DAY) return "MMM 'YY";         // up to ~3 years
      return 'YYYY';                                        // multi-year
    },

    // For non-date category labels (e.g. month names already supplied by the
    // API). Keep them as-is; only tidy obvious ISO date strings if any slip in.
    categoryLabel(val) {
      const s = '' + val;
      if (this.labelsAreDates) return moment(s).format(this.timeFormatPattern());
      return s;
    },

    // The tracking API returns `labels` as date strings paired with flat value
    // arrays (data[i][idx] aligns to labels[idx]); some feeds instead embed the
    // timestamp on each point ({x|t|date, y|value}). Detect which so we can use
    // a real time axis in both cases.
    pointTimestamp(p, idx) {
      if (p && typeof p === 'object') {
        const t = p.x != null ? p.x : (p.t != null ? p.t : (p.date != null ? p.date : null));
        if (t != null) return moment(t).valueOf();
      }
      if (this.labels[idx] != null && this.labelsAreDates) return moment(this.labels[idx]).valueOf();
      return null;
    },

    pointValue(p) {
      if (p && typeof p === 'object') return p.y != null ? p.y : p.value;
      return p;
    },

    // Normalise time-based readings before plotting:
    //   - sort chronologically (the API can return labels in insertion order,
    //     which makes a time-axis line zig-zag backwards through time)
    //   - collapse duplicates, keeping the latest:
    //       * always: entries sharing the exact same timestamp
    //       * one-per-day types (e.g. weight) on multi-day views: the latest
    //         entry per calendar day, so several weigh-ins show as one point
    // Operates on the shared labels/data index structure the API returns
    // (labels[idx] is the timestamp; data[i][idx] the value for series i).
    dedupeReadings() {
      if (!this.labelsAreDates || !this.labels.length) return;

      const isDayView = this.dateStep === 0 || this.dateStep === 1;
      const perDay = ONE_PER_DAY_TYPES.indexOf(this.type) !== -1 && !isDayView;

      const chosen = {}; // bucket key -> index kept so far
      for (let idx = 0; idx < this.labels.length; idx++) {
        const ts = moment(this.labels[idx]).valueOf();
        if (isNaN(ts)) continue;
        const key = perDay ? moment(this.labels[idx]).format('YYYY-MM-DD') : ts;
        const prev = chosen[key];
        if (prev == null || ts >= moment(this.labels[prev]).valueOf()) chosen[key] = idx;
      }

      // Always rebuild in chronological order (also dedupes via `chosen`).
      const keep = Object.keys(chosen).map(function (k) { return chosen[k]; });
      keep.sort(function (a, b) { return moment(this.labels[a]).valueOf() - moment(this.labels[b]).valueOf(); }.bind(this));

      this.labels = keep.map(function (i) { return this.labels[i]; }, this);
      this.data = this.data.map(function (arr) {
        if (!Array.isArray(arr)) return arr;
        return keep.map(function (i) { return arr[i]; });
      });
    },

    // Span (ms) between the earliest and latest timestamp in the data; used as a
    // fallback for label granularity when no explicit from/to window applies.
    dataTimeSpan() {
      let min = Infinity, max = -Infinity;
      for (let i = 0; i < this.data.length; i++) {
        const arr = this.data[i] || [];
        for (let j = 0; j < arr.length; j++) {
          const ts = this.pointTimestamp(arr[j], j);
          if (ts == null) continue;
          if (ts < min) min = ts;
          if (ts > max) max = ts;
        }
      }
      return max > min ? max - min : 0;
    },

    toPoint(p, idx) {
      const ts = this.pointTimestamp(p, idx);
      const y = this.pointValue(p);
      if (ts != null) return [ts, y];
      // Category axis: x is taken from xAxis.data (labels); plot the bare value.
      return y;
    },

    // True when the data carries usable timestamps (either embedded on points
    // or via parseable date `labels`). Cached per render in `labelsAreDates`.
    detectLabelsAreDates() {
      if (!this.labels || !this.labels.length) return false;
      let parseable = 0, considered = 0;
      for (let i = 0; i < this.labels.length; i++) {
        const l = this.labels[i];
        if (l == null) continue;
        considered++;
        // Reject plain numbers / month names — only accept real date strings.
        if (typeof l === 'number') return false;
        if (/^\d+$/.test('' + l)) return false;
        if (moment('' + l, moment.ISO_8601, false).isValid() || moment(new Date('' + l)).isValid()) parseable++;
      }
      return considered > 0 && parseable === considered;
    },

    hasTimeAxis() {
      if (this.labelsAreDates) return true;
      for (let i = 0; i < this.data.length; i++) {
        const arr = this.data[i] || [];
        for (let j = 0; j < arr.length; j++) {
          const p = arr[j];
          if (p && typeof p === 'object' && (p.x != null || p.t != null || p.date != null)) return true;
        }
      }
      return false;
    },

    // --- series styling -----------------------------------------------------

    seriesColor(index) {
      let color = GraphManager.graphLineColorByType(this, index);
      if (!color && this.override[index] && this.override[index].borderColor) {
        color = this.override[index].borderColor;
      }
      // Never draw a white / near-white line on the white card background
      // (affects e.g. mood & sleep, whose colours come through as #FFFFFF).
      // Fall back to the type's brand colour first, then a distinct palette.
      if (!color || this.isWhiteish(color)) {
        const brand = TYPE_BRAND_COLORS[this.type];
        color = (index === 0 && brand) ? brand : FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
      }
      return color;
    },

    isWhiteish(color) {
      const c = ('' + color).trim().toLowerCase();
      if (c === 'white' || c === '#fff' || c === '#ffffff') return true;
      const rgb = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
      if (rgb) return +rgb[1] >= 240 && +rgb[2] >= 240 && +rgb[3] >= 240;
      const hex = c.match(/^#([0-9a-f]{6})$/);
      if (hex) {
        const n = parseInt(hex[1], 16);
        return ((n >> 16) & 255) >= 240 && ((n >> 8) & 255) >= 240 && (n & 255) >= 240;
      }
      return false;
    },

    areaGradient(color) {
      return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: this.withAlpha(color, 0.32) },
        { offset: 1, color: this.withAlpha(color, 0.02) }
      ]);
    },

    withAlpha(color, alpha) {
      const c = ('' + color).trim();
      const rgb = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
      if (rgb) return 'rgba(' + rgb[1] + ',' + rgb[2] + ',' + rgb[3] + ',' + alpha + ')';
      const hex = c.match(/^#([0-9a-f]{6})$/i);
      if (hex) {
        const n = parseInt(hex[1], 16);
        return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
      }
      return c;
    },

    isDashed(index) {
      if (this.type === 'hba1c') return false;
      const dash = this.override[index] && this.override[index].borderDash;
      return Array.isArray(dash) && dash.some(function (n) { return n > 0; });
    },

    // --- tooltip ------------------------------------------------------------

    formatValue(label, value, index) {
      const self = this;
      if (value == null) return label;

      if (label === 'Stones & Pounds') {
        let pounds = Math.round(value * 14);
        let stones = parseInt(pounds * 0.071429, 10);
        let remainder = pounds % 14;
        return stones + 'st ' + remainder + (remainder === 1 ? 'lb' : 'lbs');
      }
      if (['minutesAsleep', 'minutesAwake', 'stageRemMinutes', 'stageDeepMinutes', 'stageLightMinutes'].indexOf(label) !== -1) {
        if (value >= 60) {
          const hours = Math.floor(value / 60);
          const minutes = value % 60;
          return moment.duration(hours, 'hours').format('h _') + ' ' + moment.duration(minutes, 'minutes').format('m _');
        }
        return moment.duration(value, 'minutes').format('m _');
      }
      if (label === 'Pounds') return value + ' lbs';
      if (label === 'Kilograms') return value + ' kg';
      if (label === 'ratio') {
        const waist = self.rawResponse && self.rawResponse.data['waist-hip'] && self.rawResponse.data['waist-hip'][index];
        let lines = ['Waist-hip ratio: ' + value];
        if (waist) {
          if (store.state.user.settings.waist.measure === 'cm') {
            lines.push('Waist: ' + waist.waist_cm + 'cm Hip: ' + waist.hip_cm + 'cm');
          } else if (store.state.user.settings.waist.measure === 'in') {
            lines.push('Waist: ' + waist.waist_in + 'in Hip: ' + waist.hip_in + 'in');
          }
        }
        return lines.join('<br/>');
      }
      if (label === 'Calories Burnt') return value + 'kcal';
      if (label === 'Systolic (mmHg)') return value + ' ' + self.$t('message.labelSystolic') + ' (mmHg)';
      if (label === 'Diastolic (mmHg)') return value + ' ' + self.$t('message.labelDiastolic') + ' (mmHg)';
      if (label === 'Basal') return value + ' ' + self.$t('message.labelBasal');
      if (label === 'Bolus') return value + ' ' + self.$t('message.labelBolus');
      if (label === 'Correction') return value + ' ' + self.$t('message.labelCorrection');
      if (label === 'Lower ketone range' || label === 'Upper range') return value + ' ' + self.$t('message.labelUpperRange');
      if (label === 'Upper ketone range' || label === 'Lower range') return value + ' ' + self.$t('message.labelLowerRange');
      if (self.type === 'medication') return value + ' ' + self.$t(self.localizeRegime(label));
      if (self.type === 'mood') {
        return label === 'Mood'
          ? value + ' ' + self.$t('message.cardMood')
          : value + ' ' + self.$t('message.labelBaseLine');
      }
      return value + ' ' + label;
    },

    tooltipFormatter(params) {
      const self = this;
      if (!params || !params.length) return '';
      const first = params[0];
      const axisValue = first.axisValue != null ? first.axisValue : (first.value && first.value[0]);
      let title;
      if (self.hasTimeAxis()) {
        // Show the time component only when the view is intraday.
        const intraday = self.axisSpanMs > 0 && self.axisSpanMs <= 2 * 24 * 3600 * 1000;
        title = moment(axisValue).format(intraday ? 'DD-MM-YYYY HH:mm a' : 'DD-MM-YYYY');
      } else {
        title = '' + axisValue;
      }
      let body = '';
      params.forEach(function (p) {
        const value = Array.isArray(p.value) ? p.value[1] : p.value;
        if (value == null) return;
        body += '<div style="display:flex;align-items:center;margin-top:4px">'
          + '<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:' + p.color + ';margin-right:8px"></span>'
          + self.formatValue(p.seriesName, value, p.dataIndex)
          + '</div>';
      });
      return '<div style="font-size:13px;opacity:0.85">' + title + '</div>' + body;
    },

    // --- render -------------------------------------------------------------

    renderChartFromData(response) {
      let self = this;
      self.rawResponse = response;
      self.data = response.data.data || [];
      self.labels = response.data.labels || [];
      self.series = response.data.series || [];
      self.override = response.data.override || [];
      self.labelsAreDates = self.detectLabelsAreDates();
      self.dedupeReadings();

      // A single series reads better with points always shown.
      if (self.series.length === 1) self.pointsVisible = true;

      const useTime = self.hasTimeAxis();
      const singleSeries = self.series.length === 1;
      const refs = clinicalRanges(self.type, store);

      // Display options from the chart's Options menu (curved line / area fill).
      const smooth = store.state.graphOptions.smoothLine ? 0.4 : false;
      const showArea = store.state.graphOptions.showArea && singleSeries;

      const echartsSeries = self.data.map(function (arr, i) {
        const color = self.seriesColor(i);
        const dashed = self.isDashed(i);
        const points = (arr || []).map(function (p, idx) { return self.toPoint(p, idx); });
        // Preserve the relative line-weight hierarchy from the API (e.g. mood's
        // primary line is heavier than its flat baseline) but keep it readable.
        const overrideWidth = self.override[i] && self.override[i].borderWidth;
        const width = overrideWidth ? Math.max(1, Math.min(overrideWidth, 3)) : 2.5;

        const s = {
          name: self.series[i] || ('Series ' + (i + 1)),
          type: 'line',
          data: points,
          smooth: smooth,
          connectNulls: true,
          showSymbol: self.pointsVisible,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: color,
            width: width,
            type: dashed ? 'dashed' : 'solid'
          },
          itemStyle: { color: color },
          emphasis: { focus: 'series' },
          z: dashed ? 2 : 3,
          // Always declared so toggling area off later merges cleanly.
          areaStyle: (showArea && !dashed) ? { color: self.areaGradient(color) } : { opacity: 0 }
        };
        return s;
      });

      // Attach clinical reference zones / thresholds to the first series.
      if (echartsSeries.length && (refs.bands.length || refs.lines.length)) {
        const target = echartsSeries[0];
        if (refs.bands.length) {
          target.markArea = {
            silent: true,
            label: {
              show: true,
              position: ['8px', '6px'],
              color: '#5FB737',
              fontFamily: FONT,
              fontSize: 11
            },
            data: refs.bands.map(function (b) {
              return [
                { yAxis: b.from, name: b.label, itemStyle: { color: b.color } },
                { yAxis: b.to }
              ];
            })
          };
        }
        if (refs.lines.length) {
          target.markLine = {
            silent: true,
            symbol: 'none',
            data: refs.lines.map(function (l) {
              return {
                yAxis: l.value,
                lineStyle: { color: l.color, type: 'dashed', width: 1.5 },
                label: {
                  formatter: l.label,
                  position: 'insideEndTop',
                  color: l.color,
                  fontFamily: FONT,
                  fontSize: 11
                }
              };
            })
          };
        }
      }

      const xAxis = {
        type: useTime ? 'time' : 'category',
        boundaryGap: false,
        axisLine: { lineStyle: { color: GRID_COLOR } },
        axisTick: { show: false },
        axisLabel: {
          color: AXIS_COLOR,
          fontFamily: FONT,
          fontSize: 12,
          margin: 12,
          hideOverlap: true,
          formatter: useTime
            ? function (val) { return moment(val).format(self.timeFormatPattern()); }
            : function (val) { return self.categoryLabel(val); }
        },
        splitLine: { show: false }
      };
      if (!useTime) {
        xAxis.data = self.labels;
        // Thin dense category labels so they never overlap.
        if (self.labels.length > 12) xAxis.axisLabel.interval = Math.ceil(self.labels.length / 8) - 1;
      } else {
        // Fixed pills frame the whole selected period (day / week / month /
        // year) so the axis reads the way the pill promises — a week always
        // spans Mon–Sun, a month always shows its full run of days, etc. — even
        // when only part of it has readings. Custom ranges instead fit to the
        // actual data extent: a wide custom window (e.g. 13 years) with only
        // recent readings would otherwise squash everything into a sliver.
        const from = moment(self.fromDate, 'YYYY-MM-DD', true);
        const to = moment(self.toDate, 'YYYY-MM-DD', true);
        const isPreset = [0, 1, 7, 30, 360].indexOf(self.dateStep) !== -1;
        if (isPreset && from.isValid() && to.isValid()) {
          xAxis.min = from.clone().startOf('day').valueOf();
          xAxis.max = to.clone().endOf('day').valueOf();
          self.axisSpanMs = xAxis.max - xAxis.min;
        } else {
          // Let ECharts auto-range to the data.
          self.axisSpanMs = self.dataTimeSpan();
        }
        // Hint enough ticks to land on calendar boundaries: ~daily for a week,
        // ~monthly for a year. ECharts still snaps these to nice dates.
        xAxis.splitNumber = self.dateStep === 7 ? 7 : (self.dateStep === 360 ? 12 : 6);
      }

      // Drag to pan + wheel/pinch to zoom along the x-axis. `filterMode: 'none'`
      // keeps the line continuous while panning instead of clipping points at
      // the edges. Disabled when there's too little data to be worth zooming.
      const totalPoints = self.data.reduce(function (n, arr) { return n + ((arr && arr.length) || 0); }, 0);
      const enableZoom = totalPoints > 2;
      const dataZoom = enableZoom ? [{
        type: 'inside',
        xAxisIndex: 0,
        filterMode: 'none',
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
        // Don't swallow the page's vertical scroll on touch unless the user is
        // actively pinch-zooming the chart.
        preventDefaultMouseMove: false
      }] : [];

      const option = {
        animationDuration: 600,
        textStyle: { fontFamily: FONT },
        grid: { left: 12, right: 18, top: 24, bottom: 12, containLabel: true },
        dataZoom: dataZoom,
        legend: {
          show: self.series.length > 1,
          top: 0,
          icon: 'circle',
          textStyle: { color: AXIS_COLOR, fontFamily: FONT, fontSize: 13 }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line', lineStyle: { color: '#B9C6CC', width: 1 } },
          backgroundColor: '#0179d5',
          borderWidth: 0,
          padding: [10, 14],
          textStyle: { color: '#fff', fontFamily: FONT, fontSize: 14 },
          formatter: function (params) { return self.tooltipFormatter(params); }
        },
        xAxis: xAxis,
        yAxis: {
          type: 'value',
          scale: !(self.type === 'blood-glucose' || self.type === 'ketones' || self.type === 'cholesterol' || self.type === 'sleep'),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: AXIS_COLOR, fontFamily: FONT, fontSize: 13 },
          splitLine: { lineStyle: { color: GRID_COLOR, type: [1, 6] } }
        },
        series: echartsSeries
      };

      self.$nextTick(function () {
        const scrollingElement = $('.main-content');
        const previousPagePosition = scrollingElement.scrollTop();
        self.ensureChart();
        if (self.chart) {
          self.chart.setOption(option, true);
          self.chart.resize();
          scrollingElement.scrollTop(previousPagePosition);
        }
        self.$emit('graphLoading', false);
        self.$emit('graphLoaded', response);
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/Tracking/Graph/Line/index.js